import sdk from "@/sdk";
import {
  DetailedWithdrawRequestRow,
  BondMetadataRow,
  UserBankInfo,
  BalanceHistoryEntry,
  RefundHistoryEntry,
} from "./types";
import { parseBondMetadataFromCSV } from "./parsers";

// Google Sheets CSV URL for bond metadata
const BOND_METADATA_URL =
  "https://docs.google.com/spreadsheets/d/e/2PACX-1vSWDU7-KiB0oEFgakRAEnfNEPnlIN3ekWuD3eSOXX76qy7z_Pos_kZJvuzUkvbGesyBnHNnDiSJRMpL/pub?output=csv";

// Fetch bond metadata from Google Sheets
export const fetchBondMetadataFromSheets = async (): Promise<
  BondMetadataRow[]
> => {
  const response = await fetch(BOND_METADATA_URL);
  const csvText = await response.text();
  return parseBondMetadataFromCSV(csvText);
};

// Search by a single term, match by email
const searchAndMatch = async (
  term: string,
  email: string,
): Promise<{ user?: Record<string, unknown>; wrongMatch?: string }> => {
  const result = await sdk.getUsers({
    page: 0,
    limit: 5,
    advanced: { search: term },
  });
  const users = (result.getUsers?.users || []) as Record<string, unknown>[];
  const matched = users.find(
    (u) => String(u.email).toLowerCase().trim() === email,
  );
  if (matched) return { user: matched };
  if (users.length > 0)
    return { wrongMatch: users.map((u) => u.email).join(", ") };
  return {};
};

// Fetch customer bond data from API
export const fetchCustomerDataFromAPI = async (
  detailedWithdrawData: DetailedWithdrawRequestRow[],
): Promise<{
  files: Record<
    string,
    {
      purchaseData: Record<string, unknown>[];
      txHistoryData: Record<string, unknown>[];
    }
  >;
  successCount: number;
  totalCount: number;
  skipped: string[];
}> => {
  // Include ALL customers, not just bond customers
  const allCustomers = [
    ...new Map(
      detailedWithdrawData.map((r) => [
        r.regNumber.trim(),
        {
          email: r.email.toLowerCase().trim(),
          regNumber: r.regNumber.trim(),
          lastName: r.lastName?.trim() || "",
          firstName: r.firstName?.trim() || "",
          phone: r.phone?.trim() || "",
        },
      ]),
    ).values(),
  ];

  const files: Record<
    string,
    {
      purchaseData: Record<string, unknown>[];
      txHistoryData: Record<string, unknown>[];
    }
  > = {};
  let successCount = 0;
  const skipped: string[] = [];
  const retryByPhone: typeof allCustomers = [];

  // Helper to fetch balance and store
  const fetchAndStore = async (
    user: Record<string, unknown>,
    regNumber: string,
  ) => {
    const balanceResult = await sdk.getBalanceByUserId({
      _id: user._id as string,
    });
    const balanceHistory =
      balanceResult.getBalanceByUserId?.balanceHistory || [];
    const orderHistory = balanceResult.getBalanceByUserId?.orderHistory || [];
    files[regNumber] = {
      purchaseData: balanceHistory.map((h: Record<string, unknown>) => ({
        ...h,
        _source: "balanceHistory",
      })),
      txHistoryData: orderHistory.map((h: Record<string, unknown>) => ({
        ...h,
        _source: "orderHistory",
      })),
    };
  };

  // PASS 1: search by regNumber
  for (const customer of allCustomers) {
    const { email, regNumber } = customer;
    try {
      const { user, wrongMatch } = await searchAndMatch(regNumber, email);
      if (user) {
        console.log(`[fetch] ✓ ${email} found by regNumber`);
        await fetchAndStore(user, regNumber);
        successCount++;
      } else if (wrongMatch) {
        console.warn(
          `[fetch] regNumber "${regNumber}" → ${wrongMatch} (not ${email}), will retry by phone`,
        );
        retryByPhone.push(customer);
      } else {
        files[regNumber] = { purchaseData: [], txHistoryData: [] };
        skipped.push(`${regNumber} (${email}): олдсонгүй`);
        successCount++;
      }
    } catch (err) {
      files[regNumber] = { purchaseData: [], txHistoryData: [] };
      const errMsg = err instanceof Error ? err.message : String(err);
      skipped.push(`${regNumber} (${email}): ${errMsg}`);
      successCount++;
    }
  }

  // PASS 2: retry failed users by phone (DB has recovered by now)
  for (const customer of retryByPhone) {
    const { email, regNumber, phone } = customer;
    if (!phone) {
      files[regNumber] = { purchaseData: [], txHistoryData: [] };
      skipped.push(`${regNumber} (${email}): олдсонгүй (утас байхгүй)`);
      successCount++;
      continue;
    }
    try {
      const { user } = await searchAndMatch(phone, email);
      if (user) {
        console.log(`[fetch] ✓ ${email} found by phone "${phone}"`);
        await fetchAndStore(user, regNumber);
        successCount++;
      } else {
        files[regNumber] = { purchaseData: [], txHistoryData: [] };
        skipped.push(`${regNumber} (${email}): утсаар ч олдсонгүй`);
        successCount++;
      }
    } catch (err) {
      files[regNumber] = { purchaseData: [], txHistoryData: [] };
      const errMsg = err instanceof Error ? err.message : String(err);
      skipped.push(`${regNumber} (${email}): ${errMsg}`);
      successCount++;
    }
  }

  return { files, successCount, totalCount: allCustomers.length, skipped };
};

export interface LookupResult {
  bankInfoMap: Record<string, UserBankInfo>;
  balanceHistoryMap: Record<string, BalanceHistoryEntry[]>;
  refundHistoryMap: Record<string, RefundHistoryEntry[]>;
}

// Lookup user bank info from database - by register number
export const lookupUserBankInfo = async (
  rows: DetailedWithdrawRequestRow[],
): Promise<LookupResult> => {
  const bankInfoMap: Record<string, UserBankInfo> = {};
  const balanceHistoryMap: Record<string, BalanceHistoryEntry[]> = {};
  const refundHistoryMap: Record<string, RefundHistoryEntry[]> = {};

  // Get unique customers by email with their regNumber
  const customerMap = new Map<
    string,
    {
      email: string;
      regNumber: string;
      lastName: string;
      firstName: string;
      phone: string;
    }
  >();
  rows.forEach((row) => {
    const email = row.email.toLowerCase().trim();
    if (email && row.regNumber) {
      customerMap.set(email, {
        email,
        regNumber: row.regNumber,
        lastName: row.lastName?.trim() || "",
        firstName: row.firstName?.trim() || "",
        phone: row.phone?.trim() || "",
      });
    }
  });

  const customers = Array.from(customerMap.values());
  let foundCount = 0;

  for (const { email, regNumber } of customers) {
    if (!regNumber) continue;
    try {
      const { user } = await searchAndMatch(regNumber, email);

      if (user) {
        const userDetail = await sdk.getUser({ _id: user._id as string });

        if (userDetail.getUser) {
          let cashTotal = 0;
          let balanceHoldAmount = 0;
          let portfolioIndex = 0;
          try {
            const balanceResult = await sdk.getBalanceByUserId({
              _id: user._id as string,
            });
            cashTotal = balanceResult.getBalanceByUserId?.cashAmount || 0;
            balanceHoldAmount =
              balanceResult.getBalanceByUserId?.holdAmount || 0;
            portfolioIndex =
              balanceResult.getBalanceByUserId?.user?.chosenPortfolio?.index ||
              0;

            const balanceHistory =
              balanceResult.getBalanceByUserId?.balanceHistory || [];
            if (balanceHistory.length > 0) {
              balanceHistoryMap[email] = balanceHistory.map(
                (h: Record<string, unknown>) => ({
                  _id: String(h._id || ""),
                  title: String(h.title || ""),
                  cashAmount: Number(h.cashAmount) || 0,
                  holdAmount: Number(h.holdAmount) || 0,
                  principleAmount: Number(h.principleAmount) || 0,
                  fee: Number(h.fee) || 0,
                  createdAt: String(h.createdAt || ""),
                  stocks: (
                    (h.stocks as Array<Record<string, unknown>>) || []
                  ).map((s) => ({
                    stock: s.stock as
                      | { name: string; symbol: string }
                      | undefined,
                    totalAmount: Number(s.totalAmount) || 0,
                    totalPrice: Number(s.totalPrice) || 0,
                    holdAmount: Number(s.holdAmount) || 0,
                    unitPrice: Number(s.unitPrice) || 0,
                    fee: Number(s.fee) || 0,
                  })),
                  bonds: (
                    (h.bonds as Array<Record<string, unknown>>) || []
                  ).map((b) => ({
                    bond: b.bond as
                      | { symbol: string; name: string }
                      | undefined,
                    principleAmount: Number(b.principleAmount) || 0,
                    fee: Number(b.fee) || 0,
                  })),
                }),
              );
            }
          } catch (balanceErr) {
            console.warn(
              `Failed to fetch balance for ${regNumber}:`,
              balanceErr,
            );
          }

          bankInfoMap[email] = {
            bankName: userDetail.getUser.bankInfo?.bankName || "",
            accountNo: userDetail.getUser.bankInfo?.accountNo || "",
            depositCode: userDetail.getUser.depositCode || "",
            lastName: userDetail.getUser.lastName || "",
            firstName: userDetail.getUser.firstName || "",
            cashTotal,
            holdAmount: balanceHoldAmount,
            portfolioIndex,
          };
          foundCount++;
        }
      }
    } catch (err) {
      console.warn(`Failed to lookup user: ${regNumber} (${email})`, err);
    }
  }

  console.log(
    `Lookup complete: ${foundCount} found out of ${customers.length}`,
  );
  return { bankInfoMap, balanceHistoryMap, refundHistoryMap };
};

// Fetch portfolio allocations map
export const fetchPortfolioAllocations = async (): Promise<
  Record<number, Record<string, number>>
> => {
  const portfolioAllocationsMap: Record<number, Record<string, number>> = {};
  try {
    const portfoliosResult = await sdk.getAllPortfolios();
    const portfolios = portfoliosResult.getAllPortfolios || [];

    portfolios.forEach(
      (portfolio: {
        index: number;
        stocks?: Array<{ stock?: { symbol: string }; value: number }>;
      }) => {
        const allocations: Record<string, number> = {};
        portfolio.stocks?.forEach((stockAlloc) => {
          if (stockAlloc.stock?.symbol && stockAlloc.value) {
            allocations[stockAlloc.stock.symbol] = stockAlloc.value / 100;
          }
        });
        portfolioAllocationsMap[portfolio.index] = allocations;
      },
    );
  } catch (err) {
    console.warn("Failed to fetch portfolios:", err);
  }
  return portfolioAllocationsMap;
};

// ============================================================
// Dual-account aware versions: dedup by EMAIL, not regNumber
// These handle cases where multiple emails share the same regNumber
// ============================================================

// Fetch customer data keyed by EMAIL (handles dual accounts with same regNumber)
export const fetchCustomerDataByEmail = async (
  detailedWithdrawData: DetailedWithdrawRequestRow[],
): Promise<{
  files: Record<
    string,
    {
      purchaseData: Record<string, unknown>[];
      txHistoryData: Record<string, unknown>[];
    }
  >;
  successCount: number;
  totalCount: number;
  skipped: string[];
}> => {
  // Dedup by EMAIL instead of regNumber
  const allCustomers = [
    ...new Map(
      detailedWithdrawData.map((r) => [
        r.email.toLowerCase().trim(),
        {
          email: r.email.toLowerCase().trim(),
          regNumber: r.regNumber.trim(),
          lastName: r.lastName?.trim() || "",
          firstName: r.firstName?.trim() || "",
          phone: r.phone?.trim() || "",
        },
      ]),
    ).values(),
  ];

  const files: Record<
    string,
    {
      purchaseData: Record<string, unknown>[];
      txHistoryData: Record<string, unknown>[];
    }
  > = {};
  let successCount = 0;
  const skipped: string[] = [];
  const retryByPhone: typeof allCustomers = [];

  const fetchAndStoreByEmail = async (
    user: Record<string, unknown>,
    email: string,
    regNumber: string,
  ) => {
    const balanceResult = await sdk.getBalanceByUserId({
      _id: user._id as string,
    });
    const balanceHistory =
      balanceResult.getBalanceByUserId?.balanceHistory || [];
    const orderHistory = balanceResult.getBalanceByUserId?.orderHistory || [];
    const data = {
      purchaseData: balanceHistory.map((h: Record<string, unknown>) => ({
        ...h,
        _source: "balanceHistory",
      })),
      txHistoryData: orderHistory.map((h: Record<string, unknown>) => ({
        ...h,
        _source: "orderHistory",
      })),
    };
    // Store by email (primary key for dual accounts)
    files[email] = data;
    // Also store by regNumber for backward compat
    if (!files[regNumber]) files[regNumber] = data;
  };

  // PASS 1: search by regNumber
  for (const customer of allCustomers) {
    const { email, regNumber } = customer;
    try {
      const { user, wrongMatch } = await searchAndMatch(regNumber, email);
      if (user) {
        console.log(`[fetch-email] ✓ ${email} found by regNumber`);
        await fetchAndStoreByEmail(user, email, regNumber);
        successCount++;
      } else if (wrongMatch) {
        console.warn(
          `[fetch-email] regNumber "${regNumber}" → ${wrongMatch} (not ${email}), will retry by phone`,
        );
        retryByPhone.push(customer);
      } else {
        files[email] = { purchaseData: [], txHistoryData: [] };
        skipped.push(`${regNumber} (${email}): олдсонгүй`);
        successCount++;
      }
    } catch (err) {
      files[email] = { purchaseData: [], txHistoryData: [] };
      const errMsg = err instanceof Error ? err.message : String(err);
      skipped.push(`${regNumber} (${email}): ${errMsg}`);
      successCount++;
    }
  }

  // PASS 2: retry by phone for dual-account cases
  for (const customer of retryByPhone) {
    const { email, regNumber, phone } = customer;
    if (!phone) {
      files[email] = { purchaseData: [], txHistoryData: [] };
      skipped.push(`${regNumber} (${email}): олдсонгүй (утас байхгүй)`);
      successCount++;
      continue;
    }
    try {
      const { user } = await searchAndMatch(phone, email);
      if (user) {
        console.log(`[fetch-email] ✓ ${email} found by phone "${phone}"`);
        await fetchAndStoreByEmail(user, email, regNumber);
        successCount++;
      } else {
        files[email] = { purchaseData: [], txHistoryData: [] };
        skipped.push(`${regNumber} (${email}): утсаар ч олдсонгүй`);
        successCount++;
      }
    } catch (err) {
      files[email] = { purchaseData: [], txHistoryData: [] };
      const errMsg = err instanceof Error ? err.message : String(err);
      skipped.push(`${regNumber} (${email}): ${errMsg}`);
      successCount++;
    }
  }

  return { files, successCount, totalCount: allCustomers.length, skipped };
};

// Lookup user bank info keyed by EMAIL (handles dual accounts)
export const lookupUserBankInfoByEmail = async (
  rows: DetailedWithdrawRequestRow[],
): Promise<LookupResult> => {
  const bankInfoMap: Record<string, UserBankInfo> = {};
  const balanceHistoryMap: Record<string, BalanceHistoryEntry[]> = {};
  const refundHistoryMap: Record<string, RefundHistoryEntry[]> = {};

  // Dedup by EMAIL
  const customerMap = new Map<
    string,
    { email: string; regNumber: string; phone: string }
  >();
  rows.forEach((row) => {
    const email = row.email.toLowerCase().trim();
    if (email && row.regNumber) {
      customerMap.set(email, {
        email,
        regNumber: row.regNumber,
        phone: row.phone?.trim() || "",
      });
    }
  });

  const customers = Array.from(customerMap.values());
  let foundCount = 0;
  const retryByPhone: typeof customers = [];

  const fetchAndStoreUser = async (
    user: Record<string, unknown>,
    email: string,
  ) => {
    const userId = String(user._id);
    const userDetail = await sdk.getUser({ _id: userId });
    if (!userDetail.getUser) return;

    let cashTotal = 0;
    let balanceHoldAmount = 0;
    let portfolioIndex = 0;
    try {
      const balanceResult = await sdk.getBalanceByUserId({ _id: userId });
      cashTotal = balanceResult.getBalanceByUserId?.cashAmount || 0;
      balanceHoldAmount = balanceResult.getBalanceByUserId?.holdAmount || 0;
      portfolioIndex =
        balanceResult.getBalanceByUserId?.user?.chosenPortfolio?.index || 0;

      const balanceHistory =
        balanceResult.getBalanceByUserId?.balanceHistory || [];
      if (balanceHistory.length > 0) {
        balanceHistoryMap[email] = balanceHistory.map(
          (h: Record<string, unknown>) => ({
            _id: String(h._id || ""),
            title: String(h.title || ""),
            cashAmount: Number(h.cashAmount) || 0,
            holdAmount: Number(h.holdAmount) || 0,
            principleAmount: Number(h.principleAmount) || 0,
            fee: Number(h.fee) || 0,
            createdAt: String(h.createdAt || ""),
            stocks: ((h.stocks as Array<Record<string, unknown>>) || []).map(
              (s) => ({
                stock: s.stock as { name: string; symbol: string } | undefined,
                totalAmount: Number(s.totalAmount) || 0,
                totalPrice: Number(s.totalPrice) || 0,
                holdAmount: Number(s.holdAmount) || 0,
                unitPrice: Number(s.unitPrice) || 0,
                fee: Number(s.fee) || 0,
              }),
            ),
            bonds: ((h.bonds as Array<Record<string, unknown>>) || []).map(
              (b) => ({
                bond: b.bond as { symbol: string; name: string } | undefined,
                principleAmount: Number(b.principleAmount) || 0,
                fee: Number(b.fee) || 0,
              }),
            ),
          }),
        );
      }
    } catch (balanceErr) {
      console.warn(`Failed to fetch balance for ${email}:`, balanceErr);
    }

    try {
      const limit = 100;
      let page = 0;
      let collected: RefundHistoryEntry[] = [];
      let totalItems = 0;
      do {
        const response = await sdk.allWithdrawalRequests({
          page,
          limit,
          status: undefined,
          search: email,
        });
        const result = response.allWithdrawalRequests;
        totalItems = result?.totalItems || 0;
        const exactMatches = (result?.requests || [])
          .filter(
            (request) =>
              String(request.user?.email || "")
                .toLowerCase()
                .trim() === email,
          )
          .map((request) => ({
            createdAt: Number(request.createdAt) || 0,
            status: String(request.status || ""),
            bondCount: Array.isArray(request.bonds) ? request.bonds.length : 0,
            stocks: [],
            bonds: [],
          }));
        collected = [...collected, ...exactMatches];
        page += 1;
      } while (page * limit < totalItems);

      refundHistoryMap[email] = collected.sort(
        (a, b) => a.createdAt - b.createdAt,
      );
    } catch (refundErr) {
      console.warn(`Failed to fetch refund history for ${email}:`, refundErr);
      refundHistoryMap[email] = [];
    }

    bankInfoMap[email] = {
      bankName: userDetail.getUser.bankInfo?.bankName || "",
      accountNo: userDetail.getUser.bankInfo?.accountNo || "",
      depositCode: userDetail.getUser.depositCode || "",
      lastName: userDetail.getUser.lastName || "",
      firstName: userDetail.getUser.firstName || "",
      cashTotal,
      holdAmount: balanceHoldAmount,
      portfolioIndex,
    };
    foundCount++;
  };

  // PASS 1: search by regNumber
  for (const customer of customers) {
    const { email, regNumber } = customer;
    if (!regNumber) continue;
    try {
      const { user, wrongMatch } = await searchAndMatch(regNumber, email);
      if (user) {
        console.log(`[lookup-email] ✓ ${email} found by regNumber`);
        await fetchAndStoreUser(user, email);
        continue;
      }

      // Fallback: search directly by email when regNumber search fails.
      const byEmail = await searchAndMatch(email, email);
      if (byEmail.user) {
        console.log(`[lookup-email] ✓ ${email} found by email fallback`);
        await fetchAndStoreUser(byEmail.user, email);
      } else if (wrongMatch) {
        console.warn(
          `[lookup-email] regNumber "${regNumber}" → ${wrongMatch} (not ${email}), will retry by phone`,
        );
        retryByPhone.push(customer);
      } else {
        console.warn(`[lookup-email] ${regNumber} (${email}): олдсонгүй`);
      }
    } catch (err) {
      console.warn(`Failed to lookup user: ${regNumber} (${email})`, err);
    }
  }

  // PASS 2: retry by phone
  for (const customer of retryByPhone) {
    const { email, regNumber, phone } = customer;
    if (!phone) {
      console.warn(
        `[lookup-email] ${regNumber} (${email}): олдсонгүй (утас байхгүй)`,
      );
      continue;
    }
    try {
      const { user } = await searchAndMatch(phone, email);
      if (user) {
        console.log(`[lookup-email] ✓ ${email} found by phone "${phone}"`);
        await fetchAndStoreUser(user, email);
      } else {
        const byEmail = await searchAndMatch(email, email);
        if (byEmail.user) {
          console.log(
            `[lookup-email] ✓ ${email} found by email fallback after phone retry`,
          );
          await fetchAndStoreUser(byEmail.user, email);
        } else {
          console.warn(
            `[lookup-email] ${regNumber} (${email}): утсаар болон email-р ч олдсонгүй`,
          );
        }
      }
    } catch (err) {
      console.warn(
        `Failed to lookup user by phone: ${regNumber} (${email})`,
        err,
      );
    }
  }

  console.log(
    `Lookup complete: ${foundCount} found out of ${customers.length}`,
  );
  return { bankInfoMap, balanceHistoryMap, refundHistoryMap };
};
